import React, { useEffect, useState } from 'react';
import { billingService } from '../../api/services/billing.service';
import {
  AlertKind,
  AlertSettingsDTO,
  BalanceAlertDTO,
  CreditGrantDTO,
  ExpiringGrantsDTO,
  GrantStatus,
  creditSourceLabels,
  grantStatusLabels,
  CreditAccountDTO,
  DailyStatementDTO,
  EnforcementDTO,
  InvoiceDTO,
  ShortfallDTO,
  SuspensionEventDTO,
  formatMoney,
} from '../../types/billing';
import { formatServerUtcAsIst, formatYmd, istToday } from './closeBooksRules';
import {
  AUTO_SUSPEND_SCOPE,
  EnforceChoice,
  alertSettingsBody,
  alertStatusClass,
  alertStatusLabels,
  enforceConfirmText,
  enforceModeLabels,
  enforcementBody,
  formatTierPercents,
  graceFloorText,
  invoiceLink,
  modeShort,
  needsEnforceConfirm,
  resultingMode,
  showShortfall,
  suspensionStateClass,
  suspensionStateLabels,
  switchGateView,
  toneClass,
} from './creditControlRules';
import { formatMinutes, istYesterday, usageKeyLabel } from './billingReportRules';
import { canVoidGrant, clampDays, grantStatusClass } from './creditGrantRules';

type Tab = 'alerts' | 'statement' | 'enforcement' | 'credits';

interface Props {
  account: CreditAccountDTO;
  /** False: settings are shown read-only (the caller has no credit reach over the account). */
  editable: boolean;
  onClose: () => void;
}

const inputCls =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-60';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const hintCls = 'text-xs text-gray-500 dark:text-gray-400 mt-1';
const sectionCls = 'border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3';
const sectionTitleCls = 'text-sm font-semibold text-gray-900 dark:text-white';
const th = 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';
const badge = 'inline-flex px-2 py-0.5 text-xs font-medium rounded-full';
const saveBtn = 'px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50';
const pagerBtn = 'px-3 py-1 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded text-sm disabled:opacity-50';

const Spinner = () => (
  <div className="flex justify-center items-center py-8">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
  </div>
);

const Pager: React.FC<{ page: number; totalPages: number; onPage: (p: number) => void }> = ({ page, totalPages, onPage }) =>
  totalPages > 1 ? (
    <div className="flex items-center justify-between pt-2">
      <p className="text-sm text-gray-600 dark:text-gray-300">Page {page} of {totalPages}</p>
      <div className="flex gap-2">
        <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1} className={pagerBtn}>Previous</button>
        <button onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className={pagerBtn}>Next</button>
      </div>
    </div>
  ) : null;

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className="text-sm font-medium text-gray-900 dark:text-white">{children}</p>
  </div>
);

// ── Alerts ────────────────────────────────────────────────────────────────────

const AlertsTab: React.FC<{ account: CreditAccountDTO; editable: boolean }> = ({ account, editable }) => {
  const orgId = account.organizationId;
  const [settings, setSettings] = useState<AlertSettingsDTO | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [useDefault, setUseDefault] = useState(true);
  const [tiersText, setTiersText] = useState('');
  const [threshold, setThreshold] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const [kind, setKind] = useState<AlertKind | ''>('');
  const [page, setPage] = useState(1);
  const [history, setHistory] = useState<BalanceAlertDTO[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const adopt = (s: AlertSettingsDTO) => {
    setSettings(s);
    setUseDefault(s.usingDefaultTiers);
    setTiersText(formatTierPercents(s.usingDefaultTiers ? s.defaultTierPercents : s.alertTierPercents));
    setThreshold(s.lowBalanceThreshold === null || s.lowBalanceThreshold === undefined ? '' : String(s.lowBalanceThreshold));
  };

  useEffect(() => {
    let live = true;
    billingService.getAlertSettings(orgId).then((r) => {
      if (!live) return;
      if (r.success && r.data) {
        adopt(r.data);
        setLoadError(null);
      } else {
        setLoadError(r.error || 'Failed to load alert settings');
      }
    });
    return () => {
      live = false;
    };
  }, [orgId]);

  useEffect(() => {
    let live = true;
    billingService.getBalanceAlerts(orgId, { kind, page, size: 20 }).then((r) => {
      if (!live) return;
      if (r.success && r.data) {
        setHistory(r.data.content);
        setTotalPages(r.data.totalPages);
        setHistoryError(null);
      } else {
        setHistoryError(r.error || 'Failed to load alerts');
      }
    });
    return () => {
      live = false;
    };
  }, [orgId, kind, page]);

  const currency = settings?.currency || account.currency || 'INR';
  const money = (v?: number | null) => formatMoney(v ?? 0, currency);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setSaved(null);
    const built = alertSettingsBody({ useDefault, tiersText, lowBalanceThreshold: threshold });
    if ('error' in built) {
      setSaveError(built.error);
      return;
    }
    setSaving(true);
    const r = await billingService.updateAlertSettings(orgId, built.body);
    setSaving(false);
    if (r.success && r.data) {
      adopt(r.data);
      setSaved('Alert settings saved.');
    } else {
      setSaveError(r.error || 'Failed to save alert settings');
    }
  };

  if (loadError) return <p className="text-sm text-red-600">{loadError}</p>;
  if (!settings) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Fact label="Available balance">{money(settings.availableBalance)}</Fact>
        <Fact label="Last top-up">
          {settings.lastTopUp
            ? `${money(settings.lastTopUp.amount)} · ${formatServerUtcAsIst(settings.lastTopUp.occurredAt)} IST`
            : 'None yet'}
        </Fact>
        <Fact label="Low-balance threshold">
          {settings.lowBalanceThreshold ? money(settings.lowBalanceThreshold) : 'None'}
        </Fact>
        <Fact label="Sent to">{settings.recipients?.length ? settings.recipients.join(', ') : 'No recipient'}</Fact>
      </div>

      <form onSubmit={save} className={sectionCls}>
        <p className={sectionTitleCls}>Alert tiers</p>
        <p className={hintCls}>
          A tier is a percentage of the last top-up: an alert goes out once when the balance falls to it, and the
          tiers re-arm on the next top-up.
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={useDefault}
            disabled={!editable}
            onChange={(e) => {
              setUseDefault(e.target.checked);
              if (e.target.checked) setTiersText(formatTierPercents(settings.defaultTierPercents));
              setSaved(null);
            }}
          />
          Use the platform default ({formatTierPercents(settings.defaultTierPercents) || 'none'}%)
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls} htmlFor="cc-tiers">Tiers (% of last top-up)</label>
            <input
              id="cc-tiers"
              value={tiersText}
              disabled={!editable || useDefault}
              placeholder="50, 20, 0"
              onChange={(e) => {
                setTiersText(e.target.value);
                setSaved(null);
              }}
              className={inputCls}
            />
            <p className={hintCls}>Comma-separated, 0–100. Empty = no percentage tiers.</p>
          </div>
          <div>
            <label className={labelCls} htmlFor="cc-threshold">Low-balance threshold ({currency})</label>
            <input
              id="cc-threshold"
              type="number"
              step="0.01"
              min="0"
              value={threshold}
              disabled={!editable}
              onChange={(e) => {
                setThreshold(e.target.value);
                setSaved(null);
              }}
              className={inputCls}
            />
            <p className={hintCls}>An extra fixed-amount tier. 0 = none.</p>
          </div>
        </div>
        {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        {saved && <p className="text-sm text-green-700 dark:text-green-400">{saved}</p>}
        {editable && (
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className={saveBtn}>{saving ? 'Saving…' : 'Save alerts'}</button>
          </div>
        )}
      </form>

      <div className={sectionCls}>
        <p className={sectionTitleCls}>Tiers since the last top-up</p>
        {settings.tiers.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No tiers.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className={`${th} text-left`}>Tier</th>
                <th className={`${th} text-right`}>Alerts at</th>
                <th className={`${th} text-left`}>Crossed</th>
                <th className={`${th} text-left`}>Alert</th>
                <th className={`${th} text-left`}>Sent (IST)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {settings.tiers.map((t) => (
                <tr key={t.tier}>
                  <td className={td}>{t.tier}</td>
                  <td className={`${td} text-right`}>{money(t.thresholdAmount)}</td>
                  <td className={td}>{t.crossed ? 'Yes' : 'No'}</td>
                  <td className={td}>
                    {t.status ? <span className={`${badge} ${alertStatusClass(t.status)}`}>{alertStatusLabels[t.status] || t.status}</span> : '-'}
                  </td>
                  <td className={td}>{t.sentAt ? formatServerUtcAsIst(t.sentAt) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={sectionCls}>
        <div className="flex items-center justify-between gap-3">
          <p className={sectionTitleCls}>History</p>
          <select
            aria-label="Alert kind"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as AlertKind | '');
              setPage(1);
            }}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm"
          >
            <option value="">All</option>
            <option value="BALANCE_ALERT">Balance alerts</option>
            <option value="DAILY_STATEMENT">Daily statements</option>
          </select>
        </div>
        {historyError && <p className="text-sm text-red-600">{historyError}</p>}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className={`${th} text-left`}>When (IST)</th>
                <th className={`${th} text-left`}>Kind</th>
                <th className={`${th} text-left`}>Tier / day</th>
                <th className={`${th} text-right`}>Balance</th>
                <th className={`${th} text-left`}>Status</th>
                <th className={`${th} text-left`}>To</th>
                <th className={`${th} text-right`}>Tries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {history.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">No alerts yet</td></tr>
              ) : (
                history.map((a) => (
                  <tr key={a.id} title={a.detail || ''}>
                    <td className={td}>{formatServerUtcAsIst(a.createdAt)}</td>
                    <td className={td}>{a.kind === 'DAILY_STATEMENT' ? 'Daily statement' : 'Balance alert'}</td>
                    <td className={td}>{a.kind === 'DAILY_STATEMENT' ? formatYmd(a.statementDate) : a.tier || '-'}</td>
                    <td className={`${td} text-right`}>{formatMoney(a.availableBalance ?? 0, a.currency || currency)}</td>
                    <td className={td}>
                      <span className={`${badge} ${alertStatusClass(a.status)}`}>{alertStatusLabels[a.status] || a.status}</span>
                    </td>
                    <td className={`${td} max-w-[16rem] truncate`}>{a.recipients || '-'}</td>
                    <td className={`${td} text-right`}>{a.attempts ?? '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pager page={page} totalPages={totalPages} onPage={setPage} />
      </div>
    </div>
  );
};

// ── Daily statement ───────────────────────────────────────────────────────────

const StatementTab: React.FC<{ account: CreditAccountDTO }> = ({ account }) => {
  const orgId = account.organizationId;
  const [date, setDate] = useState(istYesterday());
  const [statement, setStatement] = useState<DailyStatementDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!date) return;
    let live = true;
    setLoading(true);
    billingService.getDailyStatement(orgId, date).then((r) => {
      if (!live) return;
      if (r.success && r.data) {
        setStatement(r.data);
        setError(null);
      } else {
        setStatement(null);
        setError(r.error || 'Failed to load the daily statement');
      }
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [orgId, date]);

  const money = (v?: number | null) => formatMoney(v ?? 0, statement?.currency || account.currency || 'INR');
  const row = (label: string, value: React.ReactNode, strong = false) => (
    <tr>
      <td className={`${td} ${strong ? 'font-semibold' : ''}`}>{label}</td>
      <td className={`${td} text-right ${strong ? 'font-semibold' : ''}`}>{value}</td>
    </tr>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className={labelCls} htmlFor="cc-date">Day (IST)</label>
          <input
            id="cc-date"
            type="date"
            aria-label="Statement day (IST)"
            value={date}
            max={istToday()}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm"
          />
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 pb-2">00:00–24:00 IST on {formatYmd(date)}</p>
      </div>
      {loading ? (
        <Spinner />
      ) : error || !statement ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className={sectionCls}>
            <p className={sectionTitleCls}>Balance</p>
            <table className="min-w-full">
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {row('Opening balance', money(statement.openingBalance))}
                {row('+ Top-ups', money(statement.topUps))}
                {row('− Usage', money(statement.usage))}
                {row('+ Other credits', money(statement.otherCredits))}
                {row('− Other debits', money(statement.otherDebits))}
                {row('Closing balance', money(statement.closingBalance), true)}
              </tbody>
            </table>
          </div>
          <div className={sectionCls}>
            <p className={sectionTitleCls}>Usage by service</p>
            {statement.usageByService.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No usage that day.</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className={`${th} text-left`}>Service</th>
                    <th className={`${th} text-right`}>Charges</th>
                    <th className={`${th} text-right`}>Minutes</th>
                    <th className={`${th} text-right`}>Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {statement.usageByService.map((u) => (
                    <tr key={u.serviceType}>
                      <td className={td}>{usageKeyLabel(u.serviceType)}</td>
                      <td className={`${td} text-right`}>{u.charges}</td>
                      <td className={`${td} text-right`}>{formatMinutes(u.billedMinutes)}</td>
                      <td className={`${td} text-right`}>{money(u.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Shortfall (prepaid below zero) ────────────────────────────────────────────

/**
 * A prepaid account below zero (Soft lets it run there): what it owes, and a DRAFT
 * credit-purchase invoice for it (18% GST) that is finalized and collected as usual.
 * One open shortfall invoice at a time; the server answers 409 otherwise.
 */
export const ShortfallPanel: React.FC<{ orgId: string; currency: string; editable: boolean }> = ({
  orgId,
  currency,
  editable,
}) => {
  const [data, setData] = useState<ShortfallDTO | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<InvoiceDTO | null>(null);

  useEffect(() => {
    let live = true;
    billingService.getShortfall(orgId).then((r) => {
      if (!live) return;
      if (r.success && r.data) {
        setData(r.data);
        setLoadError(null);
      } else {
        setLoadError(r.error || 'Failed to load the shortfall');
      }
    });
    return () => {
      live = false;
    };
  }, [orgId, version]);

  const money = (v?: number | null) => formatMoney(v ?? 0, currency);

  const create = async () => {
    setBusy(true);
    setError(null);
    const r = await billingService.createShortfallInvoice(orgId);
    setBusy(false);
    if (r.success && r.data) {
      setCreated(r.data);
      setVersion((v) => v + 1);
    } else {
      setError(r.error || 'Failed to create the shortfall invoice');
    }
  };

  const openId = data?.openShortfallInvoiceId ?? null;

  return (
    <div className={`${sectionCls} border-orange-300 dark:border-orange-700`} data-testid="shortfall-panel">
      <p className={sectionTitleCls}>Shortfall</p>
      <p className={hintCls}>
        The balance is below zero. The shortfall is invoiced as a credit purchase (draft, 18% GST); paying it brings
        the balance back to zero.
      </p>
      {loadError ? (
        <p className="text-sm text-red-600">{loadError}</p>
      ) : !data ? (
        <Spinner />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Fact label="Balance">
              <span className={data.balance < 0 ? 'text-red-600' : ''}>{money(data.balance)}</span>
            </Fact>
            <Fact label="Shortfall (owed)">{money(data.shortfall)}</Fact>
            <Fact label="Open shortfall invoice">
              {openId ? <a className="text-indigo-600 hover:underline" href={invoiceLink(openId)}>Invoice #{openId}</a> : 'None'}
            </Fact>
          </div>
          {created && (
            <p className="text-sm text-green-700 dark:text-green-400">
              Draft invoice {created.invoiceNumber || `#${created.id}`} for {formatMoney(created.totalAmount, created.currency || currency)}{' '}
              created.{' '}
              <a className="text-indigo-600 hover:underline" href={invoiceLink(created.id)}>Open it in Invoices</a> to
              finalize it.
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {editable && (
            <div className="flex items-center justify-end gap-3">
              {openId && !created && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  One is already open: <a className="text-indigo-600 hover:underline" href={invoiceLink(openId)}>invoice #{openId}</a>
                </span>
              )}
              <button
                type="button"
                onClick={create}
                disabled={busy || !!openId || !(data.shortfall > 0)}
                className={saveBtn}
              >
                {busy ? 'Creating…' : 'Create shortfall invoice'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Enforcement ───────────────────────────────────────────────────────────────

const EnforcementTab: React.FC<{ account: CreditAccountDTO; editable: boolean }> = ({ account, editable }) => {
  const orgId = account.organizationId;
  const [state, setState] = useState<EnforcementDTO | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<EnforceChoice>('DEFAULT');
  const [grace, setGrace] = useState('');
  const [autoSuspend, setAutoSuspend] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  /** Turning Enforce on asks first, in the page. */
  const [confirmingEnforce, setConfirmingEnforce] = useState(false);

  const [events, setEvents] = useState<SuspensionEventDTO[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [eventsVersion, setEventsVersion] = useState(0);

  const adopt = (e: EnforcementDTO) => {
    setState(e);
    setMode(e.enforceMode || 'DEFAULT');
    setGrace(e.graceAmount === null || e.graceAmount === undefined ? '' : String(e.graceAmount));
    setAutoSuspend(!!e.autoSuspend);
  };

  useEffect(() => {
    let live = true;
    billingService.getEnforcement(orgId).then((r) => {
      if (!live) return;
      if (r.success && r.data) {
        adopt(r.data);
        setLoadError(null);
      } else {
        setLoadError(r.error || 'Failed to load enforcement');
      }
    });
    return () => {
      live = false;
    };
  }, [orgId]);

  useEffect(() => {
    let live = true;
    billingService.getSuspensionEvents(orgId, { page, size: 20 }).then((r) => {
      if (!live) return;
      if (r.success && r.data) {
        setEvents(r.data.content);
        setTotalPages(r.data.totalPages);
        setEventsError(null);
      } else {
        setEventsError(r.error || 'Failed to load suspension events');
      }
    });
    return () => {
      live = false;
    };
  }, [orgId, page, eventsVersion]);

  const currency = state?.currency || account.currency || 'INR';
  const money = (v?: number | null) => formatMoney(v ?? 0, currency);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    await submit(false);
  };

  const submit = async (confirmed: boolean) => {
    setSaveError(null);
    setSaved(null);
    const built = enforcementBody({ mode, graceAmount: grace, autoSuspend });
    if ('error' in built) {
      setSaveError(built.error);
      return;
    }
    if (!confirmed && state && needsEnforceConfirm(state, mode)) {
      setConfirmingEnforce(true);
      return;
    }
    setConfirmingEnforce(false);
    setSaving(true);
    const r = await billingService.updateEnforcement(orgId, built.body);
    setSaving(false);
    if (r.success && r.data) {
      adopt(r.data);
      setSaved('Enforcement saved.');
      setEventsVersion((v) => v + 1);
    } else {
      setSaveError(r.error || 'Failed to save enforcement');
    }
  };

  if (loadError) return <p className="text-sm text-red-600">{loadError}</p>;
  if (!state) return <Spinner />;

  const gate = switchGateView(state.switchGate);
  const billingType = (state.billingType as string) || account.billingType;
  const balance = state.availableBalance ?? account.creditBalance;
  const chosen = resultingMode(mode, state.globalEnforce);

  return (
    <div className="space-y-4">
      <div className={sectionCls}>
        <div className="flex flex-wrap items-center gap-3">
          <p className={sectionTitleCls}>Now</p>
          <span className={`${badge} ${suspensionStateClass(state.suspensionState)}`}>
            {suspensionStateLabels[state.suspensionState] || state.suspensionState}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Fact label="Effective mode">{modeShort(state.effectiveMode)}</Fact>
          <Fact label="This account">{state.enforceMode ? modeShort(state.enforceMode) : `Platform default (${modeShort(state.globalEnforce)})`}</Fact>
          <Fact label="Platform allows up to">{modeShort(state.maxMode)}</Fact>
          <Fact label="Available balance">{money(state.availableBalance)}</Fact>
          <Fact label="Grace">{money(state.graceAmount)}</Fact>
          <Fact label="Refuses below">{state.floor === null || state.floor === undefined ? '-' : money(state.floor)}</Fact>
          <Fact label="Auto-suspend">{state.autoSuspend ? 'On' : 'Off'}</Fact>
          <Fact label="Active locks">{state.activeLockIds?.length ? state.activeLockIds.join(', ') : 'None'}</Fact>
        </div>
        <div data-testid="switch-gate">
          <p className="text-xs text-gray-500 dark:text-gray-400">Switch calls (tata switches)</p>
          <span className={`${badge} ${toneClass[gate.tone]}`}>{gate.text}</span>
          {gate.detail && <p className={hintCls}>{gate.detail}</p>}
        </div>
      </div>

      {showShortfall(billingType, balance) && (
        <ShortfallPanel orgId={orgId} currency={currency} editable={editable} />
      )}

      <form onSubmit={save} className={sectionCls}>
        <p className={sectionTitleCls}>Settings</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls} htmlFor="cc-mode">Mode</label>
            <select
              id="cc-mode"
              value={mode}
              disabled={!editable}
              onChange={(e) => {
                setMode(e.target.value as EnforceChoice);
                setSaved(null);
                setConfirmingEnforce(false);
              }}
              className={inputCls}
            >
              <option value="DEFAULT">Platform default ({modeShort(state.globalEnforce)})</option>
              {(Object.keys(enforceModeLabels) as (keyof typeof enforceModeLabels)[]).map((m) => (
                <option key={m} value={m}>{enforceModeLabels[m]}</option>
              ))}
            </select>
            <p className={hintCls} data-testid="mode-hint">
              {chosen && enforceModeLabels[chosen as keyof typeof enforceModeLabels]
                ? enforceModeLabels[chosen as keyof typeof enforceModeLabels]
                : 'Follows the platform default.'}
            </p>
          </div>
          <div>
            <label className={labelCls} htmlFor="cc-grace">Grace ({currency})</label>
            <input
              id="cc-grace"
              type="number"
              step="0.01"
              min="0"
              value={grace}
              disabled={!editable}
              onChange={(e) => {
                setGrace(e.target.value);
                setSaved(null);
              }}
              className={inputCls}
            />
            <p className={hintCls} data-testid="grace-floor">{graceFloorText(grace, currency)}</p>
          </div>
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={autoSuspend}
              disabled={!editable}
              onChange={(e) => {
                setAutoSuspend(e.target.checked);
                setSaved(null);
              }}
            />
            Auto-suspend below the floor
          </label>
          <p className="text-xs text-orange-700 dark:text-orange-300 mt-1" data-testid="auto-suspend-scope">
            {AUTO_SUSPEND_SCOPE}
          </p>
        </div>
        {confirmingEnforce && (
          <div
            className="rounded-lg border p-3 space-y-2 bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800"
            data-testid="enforce-confirm"
          >
            <p className="text-sm text-red-800 dark:text-red-300">
              {enforceConfirmText(account.organizationName || orgId, Number(grace) || 0, currency)}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmingEnforce(false)}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => submit(true)}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Switch to Enforce'}
              </button>
            </div>
          </div>
        )}
        {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        {saved && <p className="text-sm text-green-700 dark:text-green-400">{saved}</p>}
        {editable && !confirmingEnforce && (
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className={saveBtn}>{saving ? 'Saving…' : 'Save enforcement'}</button>
          </div>
        )}
      </form>

      <div className={sectionCls}>
        <p className={sectionTitleCls}>Suspension events</p>
        {eventsError && <p className="text-sm text-red-600">{eventsError}</p>}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className={`${th} text-left`}>When (IST)</th>
                <th className={`${th} text-left`}>Action</th>
                <th className={`${th} text-left`}>Mode</th>
                <th className={`${th} text-left`}>Applied</th>
                <th className={`${th} text-right`}>Balance</th>
                <th className={`${th} text-right`}>Floor</th>
                <th className={`${th} text-left`}>Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {events.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">No suspension events</td></tr>
              ) : (
                events.map((ev) => (
                  <tr key={ev.id} title={ev.lockId ? `Lock ${ev.lockId}` : ''}>
                    <td className={td}>{formatServerUtcAsIst(ev.createdAt)}</td>
                    <td className={td}>{ev.action}</td>
                    <td className={td}>{ev.mode}</td>
                    <td className={td}>{ev.applied ? 'Yes' : 'No (logged)'}</td>
                    <td className={`${td} text-right`}>{money(ev.availableBalance)}</td>
                    <td className={`${td} text-right`}>{money(ev.floorAmount)}</td>
                    <td className={`${td} max-w-[20rem] truncate`}>{ev.reason || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pager page={page} totalPages={totalPages} onPage={setPage} />
      </div>
    </div>
  );
};

// ── Credits (grants) ──────────────────────────────────────────────────────────

const CreditsTab: React.FC<{ account: CreditAccountDTO; editable: boolean }> = ({ account, editable }) => {
  const orgId = account.organizationId;
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<ExpiringGrantsDTO | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [status, setStatus] = useState<GrantStatus | ''>('');
  const [page, setPage] = useState(1);
  const [grants, setGrants] = useState<CreditGrantDTO[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [grantsError, setGrantsError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  /** The grant whose void is being confirmed. */
  const [voiding, setVoiding] = useState<CreditGrantDTO | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    billingService.getExpiringGrants(orgId, days).then((r) => {
      if (!live) return;
      if (r.success && r.data) {
        setSummary(r.data);
        setSummaryError(null);
      } else {
        setSummaryError(r.error || 'Failed to load expiring credit');
      }
    });
    return () => {
      live = false;
    };
  }, [orgId, days, version]);

  useEffect(() => {
    let live = true;
    billingService.getGrants({ organizationId: orgId, status, page, size: 50 }).then((r) => {
      if (!live) return;
      if (r.success && r.data) {
        setGrants(r.data.content);
        setTotalPages(r.data.totalPages);
        setGrantsError(null);
      } else {
        setGrantsError(r.error || 'Failed to load credit grants');
      }
    });
    return () => {
      live = false;
    };
  }, [orgId, status, page, version]);

  const currency = summary?.currency || account.currency || 'INR';
  const money = (v?: number | null, c?: string) => formatMoney(v ?? 0, c || currency);

  const confirmVoid = async () => {
    if (!voiding) return;
    setBusy(true);
    setVoidError(null);
    const r = await billingService.voidGrant(voiding.id, reason.trim() || undefined);
    setBusy(false);
    if (r.success) {
      setNotice(`Grant #${voiding.id} voided.`);
      setVoiding(null);
      setReason('');
      setVersion((v) => v + 1);
    } else {
      setVoidError(r.error || 'Failed to void the grant');
    }
  };

  return (
    <div className="space-y-4">
      <div className={sectionCls}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className={sectionTitleCls}>Balance and expiring credit</p>
          <label className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
            Expiring within
            <input
              type="number"
              min="1"
              max="365"
              aria-label="Expiring within days"
              value={days}
              onChange={(e) => setDays(clampDays(Number(e.target.value)))}
              className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm"
            />
            days
          </label>
        </div>
        {summaryError ? (
          <p className="text-sm text-red-600">{summaryError}</p>
        ) : !summary ? (
          <Spinner />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Fact label="Balance">{money(summary.creditBalance)}</Fact>
            <Fact label="Granted credit remaining">{money(summary.grantedRemaining)}</Fact>
            <Fact label="Overdraft (owed)">
              <span className={summary.overdraft > 0 ? 'text-red-600' : ''}>{money(summary.overdraft)}</span>
            </Fact>
            <Fact label={`Expiring in ${summary.days} days`}>
              {money(summary.expiringAmount)} · {summary.expiringCount} grant(s)
            </Fact>
          </div>
        )}
      </div>

      <div className={sectionCls}>
        <div className="flex items-center justify-between gap-3">
          <p className={sectionTitleCls}>Grants</p>
          <select
            aria-label="Grant status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as GrantStatus | '');
              setPage(1);
            }}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm"
          >
            <option value="">All</option>
            {(Object.keys(grantStatusLabels) as GrantStatus[]).map((s) => (
              <option key={s} value={s}>{grantStatusLabels[s]}</option>
            ))}
          </select>
        </div>
        {notice && <p className="text-sm text-green-700 dark:text-green-400">{notice}</p>}
        {grantsError && <p className="text-sm text-red-600">{grantsError}</p>}
        {voiding && (
          <div className="rounded-lg border p-3 space-y-2 bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800">
            <p className="text-sm text-red-800 dark:text-red-300">
              Void grant #{voiding.id} ({money(voiding.originalAmount, voiding.currency)})? Its credit is removed from the balance.
            </p>
            <input
              aria-label="Void reason"
              placeholder="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={inputCls}
            />
            {voidError && <p className="text-sm text-red-600">{voidError}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setVoiding(null);
                  setVoidError(null);
                }}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={confirmVoid}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? 'Voiding…' : 'Confirm void'}
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className={`${th} text-left`}>#</th>
                <th className={`${th} text-left`}>Source</th>
                <th className={`${th} text-left`}>Status</th>
                <th className={`${th} text-right`}>Granted</th>
                <th className={`${th} text-right`}>Remaining</th>
                <th className={`${th} text-right`}>Priority</th>
                <th className={`${th} text-left`}>Expires (IST)</th>
                <th className={`${th} text-left`}>Created (IST)</th>
                {editable && <th className={`${th} text-right`}>Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {grants.length === 0 ? (
                <tr><td colSpan={editable ? 9 : 8} className="px-3 py-6 text-center text-sm text-gray-500">No grants</td></tr>
              ) : (
                grants.map((g) => (
                  <tr key={g.id} title={g.description || ''}>
                    <td className={td}>{g.id}</td>
                    <td className={td}>{creditSourceLabels[g.source as keyof typeof creditSourceLabels] || g.source}</td>
                    <td className={td}>
                      <span className={`${badge} ${grantStatusClass(g.status)}`}>{grantStatusLabels[g.status] || g.status}</span>
                    </td>
                    <td className={`${td} text-right`}>{money(g.originalAmount, g.currency)}</td>
                    <td className={`${td} text-right`}>{money(g.remainingAmount, g.currency)}</td>
                    <td className={`${td} text-right`}>{g.priority}</td>
                    <td className={td}>{g.expiresAt ? formatServerUtcAsIst(g.expiresAt) : 'Never'}</td>
                    <td className={td}>{formatServerUtcAsIst(g.createdAt)}{g.createdBy ? ` · ${g.createdBy}` : ''}</td>
                    {editable && (
                      <td className={`${td} text-right`}>
                        {g.status === 'ACTIVE' && (
                          <button
                            type="button"
                            onClick={() => {
                              setVoiding(g);
                              setReason('');
                              setVoidError(null);
                              setNotice(null);
                            }}
                            title={canVoidGrant(g) ? 'Void this grant' : 'Partly used: the server will refuse'}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Void
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pager page={page} totalPages={totalPages} onPage={setPage} />
      </div>
    </div>
  );
};

// ── The drawer ────────────────────────────────────────────────────────────────

const TABS: { id: Tab; label: string }[] = [
  { id: 'alerts', label: 'Alerts' },
  { id: 'statement', label: 'Daily statement' },
  { id: 'enforcement', label: 'Enforcement' },
  { id: 'credits', label: 'Credits' },
];

/**
 * One account's credit controls (backend docs/BILLING.md §4): balance alert tiers and
 * history, the daily balance statement, and enforcement with its suspension audit.
 * Anyone with read reach sees them; only credit reach (the organisation above, or the
 * platform) may change them.
 */
const AccountControlsModal: React.FC<Props> = ({ account, editable, onClose }) => {
  const [tab, setTab] = useState<Tab>('alerts');
  const name = account.organizationName || account.organizationId;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-5xl max-h-[90vh] flex flex-col mx-4">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold dark:text-white">Account controls · {name}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Balance alerts, the daily statement, enforcement and credit grants. Times in IST.
              {editable ? '' : ' Read-only: these are set by the organisation that bills this account.'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none" aria-label="Close">
            &times;
          </button>
        </div>

        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-4" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.id
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400'
                  : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1">
          {tab === 'alerts' && <AlertsTab account={account} editable={editable} />}
          {tab === 'statement' && <StatementTab account={account} />}
          {tab === 'enforcement' && <EnforcementTab account={account} editable={editable} />}
          {tab === 'credits' && <CreditsTab account={account} editable={editable} />}
        </div>
      </div>
    </div>
  );
};

export default AccountControlsModal;
